import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Alert,
    AlertDescription,
    Button,
    Container,
    Grid,
    ContentGridSkeleton,
    Input,
    ContentTile,
    SectionHeader,
    Text,
    VideoPreviewDialog,
} from "@athena/ui";
import { PenLine, Search, Upload } from "lucide-react";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import type { VideoAssetsQuery } from "@/graphql/media.generated";
import { MyVideoUploadDraftsDocument, VideoAssetsCountDocument, VideoAssetsDocument } from "@/graphql/media.generated";
import { BULK_UPLOAD_PATH } from "@/pages/BulkUpload/uploadQueueContext";
import { useVideoPlayback } from "@/hooks/useVideoPlayback";
import { usePlatformAdmin } from "@/lib/usePlatformAdmin";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { DEFAULT_PAGE_SIZE, useWindowedRows } from "@/hooks/useWindowedRows";
import { useListView } from "@/hooks/useListView";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { ListViewControls } from "@/components/tables/ListViewControls";

export function LibraryPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    /** The asset being previewed, or null when the dialog is closed. */
    const [
        previewing,
        setPreviewing,
    ] = useState<{
        id: string;
        title: string;
        organizationName: string;
        tags: { name: string }[];
        description: string;
        createdAt: string;
    } | null>(null);

    const { isPlatformAdmin, organizationId, loading: meLoading } = usePlatformAdmin();

    /**
     * **Searched by the server, not in the browser.** The list is a window now, so filtering the
     * rows in hand searches only what has been scrolled — which is why `videoAssetsCount` carries
     * a `search` argument too, and why its schema comment says so.
     *
     * Debounced while typing, immediate when emptied: clearing is a decision, not a keystroke.
     * One object for both queries, because a count with different arguments answers a different
     * question than the list above it.
     */
    const settledSearch = useDebouncedValue(search);
    const term = search.trim() === "" ? "" : settledSearch.trim();
    const queryVars = useMemo(() => (term ? { search: term } : {}), [term]);

    const {
        rows: allAssets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
    } = useWindowedRows<VideoAssetsQuery["videoAssets"][number]>(
        VideoAssetsDocument,
        (d) => (d as VideoAssetsQuery | undefined)?.videoAssets,
        {
            variables: queryVars,
            skip: meLoading || (!isPlatformAdmin && !organizationId),
        },
    );
    const { data: countData } = useQuery(VideoAssetsCountDocument, {
        variables: queryVars,
        skip: meLoading || (!isPlatformAdmin && !organizationId),
    });
    const total = countData?.videoAssetsCount;
    /*
     * The viewer's own unfinished uploads. Drafts are not listed below — that is what keeps a video
     * nobody has described out of the course pickers — so without this they would be invisible here.
     */
    const { data: draftsData } = useQuery(MyVideoUploadDraftsDocument, {
        fetchPolicy: "network-only",
        skip: meLoading,
    });
    const draftCount = draftsData?.myVideoUploadDrafts.length ?? 0;

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(allAssets, { hasMore, loadMore, pageSize: DEFAULT_PAGE_SIZE });
    const sentinelRef = useInfiniteScroll(loadMore, hasMore);
    const assets = view.rows;

    const preview = useVideoPlayback(previewing?.id ?? null);
    const filtered = useMemo(() => {
        // A failure a lesson still presents is the opposite case and stays: a course is pointing at
        // a video that cannot play, and somebody has to go fix that.
        return assets.filter((a) => !(a.status === "FAILED" && a.usedInCount === 0));
    }, [assets]);

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={t("common.library")}
                title={t("common.videoLib")}
                description={t("admin.lms.subTitle")}
            />
            <div className="mb-6 flex items-center gap-3">
                <div className="relative max-w-sm flex-1">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                        placeholder={t("admin.data.searchVideos")}
                        aria-label={t("admin.data.searchVideos")}
                        className="pl-9"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                    />
                </div>
                <Button className="ml-auto" onClick={() => navigate(BULK_UPLOAD_PATH)}>
                    <Upload className="mr-1.5 h-4 w-4" />
                    {t("admin.bulkUpload.openUploader")}
                </Button>
            </div>
            {draftCount > 0 && (
                <Alert className="mb-6 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                        <PenLine className="h-4 w-4" />
                        <AlertDescription>
                            {t("admin.bulkUpload.libraryDrafts", { count: draftCount })}
                        </AlertDescription>
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigate(BULK_UPLOAD_PATH)}>
                        {t("admin.bulkUpload.libraryDraftsAction")}
                    </Button>
                </Alert>
            )}
            {meLoading || loading ? (
                <ContentGridSkeleton />
            ) : filtered.length === 0 ? (
                <Text muted className="flex h-32 items-center justify-center text-sm">
                    {t("admin.lms.noUploadsYet")}
                </Text>
            ) : (
                <>
                    <ListViewControls
                        view={view}
                        noun="videos"
                        count={total === undefined ? undefined : `${view.from}–${view.to} of ${total}`}
                    />
                    <Grid cols={3} gap="md" className="w-full items-stretch">
                        {filtered.map((asset) => {
                            // A filename is an upload detail. People should only ever see it if the
                            // asset predates titles and has nothing better.
                            const displayName = asset.title?.trim() || asset.originalFilename || t("admin.lms.video");
                            const orgLabel = asset.organizationId === "global-library" ? "Global Library" : "";
                            return (
                                <ContentTile
                                    key={asset.id}
                                    title={displayName}
                                    posterUrl={asset.posterUrl}
                                    isPlaylist={false}
                                    durationSeconds={asset.durationSeconds}
                                    organizationName={orgLabel}
                                    processingStatus={asset.status}
                                    tags={asset.tags}
                                    onPlayClicked={
                                        asset.status === "FAILED"
                                            ? undefined
                                            : () =>
                                                  setPreviewing({
                                                      id: asset.id,
                                                      title: displayName,
                                                      description: asset.description ?? "",
                                                      organizationName: orgLabel,
                                                      tags: asset.tags,
                                                      createdAt: asset.createdAt,
                                                  })
                                    }
                                />
                            );
                        })}
                    </Grid>
                    {/* Fetches the next page as it nears the viewport; removed at the end of the list. */}
                    {view.mode === "scroll" && hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
                    {view.mode === "scroll" && !hasMore && (
                        <div
                            ref={sentinelRef}
                            aria-hidden
                            className="text-muted-foreground h-px py-4 text-center text-sm"
                        >
                            You've reached the end of the list! ✨
                        </div>
                    )}
                    {loadingMore && <div className="text-muted-foreground py-4 text-center text-sm">Loading more…</div>}
                </>
            )}
            <VideoPreviewDialog
                open={previewing !== null}
                title={previewing?.title ?? ""}
                description={previewing?.description ?? ""}
                organizationName={previewing?.organizationName}
                tags={previewing?.tags}
                createdAt={previewing?.createdAt}
                playback={preview.playback}
                loading={preview.loading}
                errorMessage={preview.errorMessage}
                onClose={() => setPreviewing(null)}
            />
        </Container>
    );
}
